import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Device } from '../services/Device';
import { AudioSystemState } from '../services/audio';
// import { DeviceStatus } from '../services/deviceStatusService';

// 定义设备音频状态类型
interface DeviceAudioStatus {
  deviceId: string;
  isActive: boolean;
  isPlaying: boolean;
  isRecording: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  recordingDuration: number;
  audioLevel: number;
}

// 定义设备音频统计类型
interface DeviceAudioStats {
  totalPacketsSent: number;
  totalBytesSent: number;
  totalPacketsReceived: number;
  totalBytesReceived: number;
  totalBytesPlayed: number;
  averageLatency: number;
  connectionQuality: string;
  uptime: number;
}

// 定义与后端一致的User类型
interface User {
  id: number; // 与后端一致，使用number类型
  username: string;
  email?: string; // email字段可以为空
}

// 扩展的统计信息接口，包含音频相关统计
interface ExtendedDeviceStatistics {
  totalDevices: number;
  onlineDevices: number;
  devicesWithUdpPort: number;
}

// 统一的应用状态接口
interface UnifiedAppState {
  // 设备管理状态
  devices: {
    list: Device[];
    selectedDevice: Device | null;
    isLoading: boolean;
    error: string | null;
    statistics: ExtendedDeviceStatistics | null;
    isConnected: boolean;
  };
  
  // 音频系统状态
  audio: {
    systemState: AudioSystemState;
    deviceStatus: DeviceAudioStatus | null;
    deviceStats: DeviceAudioStats | null;
    isInitialized: boolean;
    error: string | null;
    audioLevel: number;
    audioSpectrum: number[];
  };
  
  // PTT控制状态
  ptt: {
    isActive: boolean;
    /** 当前 MQTT PTT 会话所属设备（库表 deviceId 字符串）；与 isActive 同时为真时应有值 */
    activeDeviceId: string | null;
    isLoading: boolean;
    error: string | null;
    lastCommand: string;
    pressDuration: number;
  };
  
  // 用户认证状态
  auth: {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
  };
  
  // Actions
  actions: {
    // 设备管理
    setDevices: (devices: Device[]) => void;
    selectDevice: (device: Device) => void;
    setDeviceLoading: (loading: boolean) => void;
    setDeviceError: (error: string | null) => void;
    setDeviceStatistics: (statistics: ExtendedDeviceStatistics) => void;
    setDeviceConnectionStatus: (isConnected: boolean) => void;
    
    // 音频管理
    updateAudioSystemState: (state: AudioSystemState) => void;
    updateDeviceAudioStatus: (status: DeviceAudioStatus) => void;
    updateDeviceAudioStats: (stats: DeviceAudioStats) => void;
    setAudioError: (error: string | null) => void;
    setAudioInitialized: (initialized: boolean) => void;
    updateAudioLevel: (level: number) => void;
    updateAudioSpectrum: (spectrum: number[]) => void;
    
    // PTT管理
    setPttActive: (active: boolean, activeDeviceId?: string | null) => void;
    setPttLoading: (loading: boolean) => void;
    setPttError: (error: string | null) => void;
    updatePttCommand: (command: string) => void;
    updatePressDuration: (duration: number) => void;
    
    // 认证管理
    setUser: (user: User | null) => void;
    setAuthLoading: (loading: boolean) => void;
    setAuthError: (error: string | null) => void;
  };
}

export const useUnifiedStore = create<UnifiedAppState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    devices: {
      list: [],
      selectedDevice: null,
      isLoading: false,
      error: null,
      statistics: null,
      isConnected: false,
    },
    audio: {
      systemState: {
        isInitialized: false,
        activeDeviceId: null,
        microphonePermission: 'prompt',
        isConnected: false,
        isRecording: false,
        isListening: false,
        devices: {}
      },
      deviceStatus: null,
      deviceStats: null,
      isInitialized: false,
      error: null,
      audioLevel: 0,
      audioSpectrum: [],
    },
    ptt: {
      isActive: false,
      activeDeviceId: null,
      isLoading: false,
      error: null,
      lastCommand: '',
      pressDuration: 0,
    },
    auth: {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    },
    
    // Actions
    actions: {
      // 设备管理 Actions
      setDevices: (devices) => set((state) => ({
        devices: { ...state.devices, list: devices }
      })),
      selectDevice: (device) => set((state) => ({
        devices: { ...state.devices, selectedDevice: device }
      })),
      setDeviceLoading: (loading) => set((state) => ({
        devices: { ...state.devices, isLoading: loading }
      })),
      setDeviceError: (error) => set((state) => ({
        devices: { ...state.devices, error }
      })),
      setDeviceStatistics: (statistics) => set((state) => ({
        devices: { ...state.devices, statistics }
      })),
      setDeviceConnectionStatus: (isConnected) => set((state) => ({
        devices: { ...state.devices, isConnected }
      })),
      
      // 音频管理 Actions
      updateAudioSystemState: (systemState) => set((state) => ({
        audio: { ...state.audio, systemState }
      })),
      updateDeviceAudioStatus: (deviceStatus) => set((state) => ({
        audio: { ...state.audio, deviceStatus }
      })),
      updateDeviceAudioStats: (deviceStats) => set((state) => ({
        audio: { ...state.audio, deviceStats }
      })),
      setAudioError: (error) => set((state) => ({
        audio: { ...state.audio, error }
      })),
      setAudioInitialized: (isInitialized) => set((state) => ({
        audio: { ...state.audio, isInitialized }
      })),
      updateAudioLevel: (audioLevel) => set((state) => ({
        audio: { ...state.audio, audioLevel }
      })),
      updateAudioSpectrum: (audioSpectrum) => set((state) => ({
        audio: { ...state.audio, audioSpectrum }
      })),
      
      // PTT管理 Actions
      setPttActive: (isActive, activeDeviceId) =>
        set((state) => {
          if (!isActive) {
            return {
              ptt: {
                ...state.ptt,
                isActive: false,
                activeDeviceId: null,
              },
            };
          }
          const nextId =
            activeDeviceId != null && String(activeDeviceId) !== ''
              ? String(activeDeviceId)
              : state.ptt.activeDeviceId;
          return {
            ptt: {
              ...state.ptt,
              isActive: true,
              activeDeviceId: nextId,
            },
          };
        }),
      setPttLoading: (isLoading) => set((state) => ({
        ptt: { ...state.ptt, isLoading }
      })),
      setPttError: (error) => set((state) => ({
        ptt: { ...state.ptt, error }
      })),
      updatePttCommand: (lastCommand) => set((state) => ({
        ptt: { ...state.ptt, lastCommand }
      })),
      updatePressDuration: (pressDuration) => set((state) => ({
        ptt: { ...state.ptt, pressDuration }
      })),
      
      // 认证管理 Actions
      setUser: (user) => set((state) => ({
        auth: { ...state.auth, user, isAuthenticated: !!user }
      })),
      setAuthLoading: (isLoading) => set((state) => ({
        auth: { ...state.auth, isLoading }
      })),
      setAuthError: (error) => set((state) => ({
        auth: { ...state.auth, error }
      })),
    },
  }))
);
